import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  Slider,
  useTheme,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material';
import { motion } from 'framer-motion';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const formeJuridique = [
  { value: 'sasu', label: 'SASU' },
  { value: 'eurl', label: 'EURL' },
  { value: 'sarl', label: 'SARL' },
  { value: 'sa', label: 'SA' },
];

const regimeFiscal = [
  { value: 'is', label: 'Impôt sur les Sociétés (IS)' },
  { value: 'ir', label: "Impôt sur le Revenu (IR)" },
];

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

const SimulationResult = ({ data }) => {
  const theme = useTheme();

  const chartData = [
    { name: 'Chiffre d\'affaires', montant: data.ca },
    { name: 'Charges', montant: data.charges },
    { name: 'Résultat', montant: data.ca - data.charges },
  ];

  const pieData = [
    { name: 'IS', value: data.is },
    { name: 'Charges sociales', value: data.chargesSociales },
    { name: 'TVA', value: data.tva },
    { name: 'Net', value: data.ca - data.charges - data.is - data.chargesSociales - data.tva },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              height: '100%',
              background: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
            }}
          >
            <Typography variant="h6" gutterBottom>Répartition des charges</Typography>
            <Box sx={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    fill="#8884d8"
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              height: '100%',
              background: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
            }}
          >
            <Typography variant="h6" gutterBottom>Analyse financière</Typography>
            <Box sx={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="montant" fill={theme.palette.primary.main} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            {[
              { label: "Chiffre d'affaires", value: data.ca, color: theme.palette.primary.main },
              { label: "Charges totales", value: data.charges, color: theme.palette.error.main },
              { label: "Impôt sur les Sociétés", value: data.is, color: theme.palette.warning.main },
              { label: "Charges sociales", value: data.chargesSociales, color: theme.palette.info.main },
              { label: "TVA", value: data.tva, color: theme.palette.secondary.main },
              { label: "Résultat net", value: data.ca - data.charges - data.is - data.chargesSociales - data.tva, color: theme.palette.success.main },
            ].map((item, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    background: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary">
                      {item.label}
                    </Typography>
                    <Typography variant="h4" sx={{ color: item.color, fontWeight: 'bold' }}>
                      {item.value.toLocaleString()}€
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>
    </motion.div>
  );
};

const Simulation = () => {
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    formeJuridique: '',
    regimeFiscal: '',
    ca: 0,
    charges: 0,
    salaires: 0,
  });
  const [result, setResult] = useState(null);

  const handleChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      handleSimulate();
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSimulate = async () => {
    setLoading(true);
    // Simuler un appel API
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    const ca = parseFloat(formData.ca);
    const charges = parseFloat(formData.charges);
    const salaires = parseFloat(formData.salaires);
    
    // Calculs simplifiés
    const is = (ca - charges) * 0.25;
    const chargesSociales = salaires * 0.45;
    const tva = ca * 0.2;

    setResult({
      ca,
      charges,
      is,
      chargesSociales,
      tva,
    });
    
    setLoading(false);
  };

  const steps = ['Informations générales', 'Données financières', 'Résultats'];

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Forme juridique"
                value={formData.formeJuridique}
                onChange={handleChange('formeJuridique')}
              >
                {formeJuridique.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Régime fiscal"
                value={formData.regimeFiscal}
                onChange={handleChange('regimeFiscal')}
              >
                {regimeFiscal.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        );
      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography gutterBottom>Chiffre d'affaires prévisionnel</Typography>
              <Slider
                value={formData.ca}
                onChange={(e, value) => setFormData(prev => ({ ...prev, ca: value }))}
                min={0}
                max={1000000}
                step={1000}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => `${value.toLocaleString()}€`}
              />
              <TextField
                fullWidth
                label="Chiffre d'affaires"
                value={formData.ca}
                onChange={handleChange('ca')}
                type="number"
                sx={{ mt: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Charges prévisionnelles"
                value={formData.charges}
                onChange={handleChange('charges')}
                type="number"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Masse salariale"
                value={formData.salaires}
                onChange={handleChange('salaires')}
                type="number"
              />
            </Grid>
          </Grid>
        );
      case 2:
        return loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : result ? (
          <SimulationResult data={result} />
        ) : null;
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        p: 3,
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.primary.dark}15 100%)`,
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Typography variant="h4" sx={{ mb: 4, fontWeight: 'bold' }}>
          Simulation Fiscale
        </Typography>
      </motion.div>

      <Paper
        elevation={0}
        sx={{
          p: 4,
          background: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(10px)',
          borderRadius: 2,
          border: '1px solid rgba(255, 255, 255, 0.3)',
        }}
      >
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box sx={{ mt: 4, mb: 4 }}>
          {getStepContent(activeStep)}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          {activeStep > 0 && (
            <Button onClick={handleBack}>
              Précédent
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={loading}
          >
            {activeStep === steps.length - 1 ? 'Simuler' : 'Suivant'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default Simulation; 